// Project Sentinel - TypeScript Types

// ----- SEN-79: Bulk PDF invoice upload (flow=bulk_invoice) -----
//
// Backend endpoints (POST /pdf-upload/bulk, GET /pdf-upload/bulk-grid,
// POST /pdf-upload/bulk-confirm) follow the schema below. While the
// dedicated grid + bulk-confirm endpoints are still in flight we derive
// `BulkGridRow[]` client-side from the existing per-upload extraction
// payload. The schema is shared so the swap is a one-line API change.

// Lease Info
export interface LeaseInfo {
  start_date?: string | null;
  end_date?: string | null;
  landlord?: string | null;
  notes?: string | null;
}

// Contact
export interface ContactInfo {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
}

// Building Attributes
export interface BuildingAttributes {
  year_built?: number | null;
  floors?: number | null;
  parking_spaces?: number | null;
  heating_system?: string | null;
}

// Opening Hours Types
export interface DayHours {
  open: string;  // HH:MM format
  close: string; // HH:MM format
}

export interface OpeningHours {
  monday?: DayHours | null;
  tuesday?: DayHours | null;
  wednesday?: DayHours | null;
  thursday?: DayHours | null;
  friday?: DayHours | null;
  saturday?: DayHours | null;
  sunday?: DayHours | null;
}

// Building Types
export interface Building {
  id: string;
  customer_id: string;
  name: string;
  street_address?: string | null;  // Optional for planned buildings
  city: string;
  state_province?: string;
  postal_code?: string | null;  // Optional for planned buildings
  country_code: string;
  latitude?: number | null;
  longitude?: number | null;
  size_sqm: number;
  building_type?: string;
  timezone: string;
  external_system_ids: Record<string, string>;
  google_place_id?: string | null;  // SEN-307: matcher Stage P1
  is_active: boolean;
  lease_count: number;
  device_count: number;
  meter_point_count: number;
  initiative_count?: number;
  initiatives_in_progress?: number;
  opening_hours?: OpeningHours | null;
  active_leases_count?: number;
  shelly_configured?: boolean;
  shelly_connection_id?: string | null;
  shelly_server_url?: string | null;
  operational_start_date?: string | null;
  operational_end_date?: string | null;
  is_planned?: boolean;
  is_closed?: boolean;
  climate_zone?: string | null;
  store_manager_name?: string | null;  // Name of the store/site manager
  store_manager_email?: string | null; // Email of the store/site manager
  eui_benchmark?: number | null;  // kWh/m²/year benchmark for building type
  eui_kwh_per_sqm?: number | null;  // Actual Energy Use Intensity (kWh/m²/year)
  // CO₂ emission factor (kg CO₂/kWh) keyed on country_code from
  // backend/app/knowledge/emissions.py. DK=0.130, SE=0.047, DE=0.380,
  // EU fallback=0.260. Use ?? FALLBACK_CO2_FACTOR (0.260) when null/undefined.
  co2_per_kwh?: number | null;
  // Area breakdown
  sales_area_sqm?: number | null;
  stockroom_area_sqm?: number | null;
  social_area_sqm?: number | null;
  other_area_sqm?: number | null;
  // JSONB fields
  lease?: LeaseInfo | null;
  contacts?: ContactInfo[] | null;
  building_attributes?: BuildingAttributes | null;
  created_at: string;
  updated_at: string;
}

export interface BuildingCreate {
  name: string;
  street_address: string;
  city: string;
  state_province?: string;
  postal_code: string;
  country_code: string;
  latitude?: number | null;
  longitude?: number | null;
  size_sqm: number;
  building_type?: string;
  timezone?: string;
  external_system_ids?: Record<string, string>;
  shelly_auth_key?: string;
  shelly_server_url?: string;
  opening_hours?: OpeningHours | null;
  climate_zone?: string | null;
  operational_start_date?: string | null;
  operational_end_date?: string | null;
  store_manager_name?: string | null;
  store_manager_email?: string | null;
  // Area breakdown
  sales_area_sqm?: number | null;
  stockroom_area_sqm?: number | null;
  social_area_sqm?: number | null;
  other_area_sqm?: number | null;
  // JSONB fields
  lease?: LeaseInfo | null;
  contacts?: ContactInfo[] | null;
  building_attributes?: BuildingAttributes | null;
}

export interface BuildingUpdate extends Partial<BuildingCreate> {
  is_active?: boolean;
  shelly_auth_key?: string;
  shelly_server_url?: string;
  opening_hours?: OpeningHours | null;
  sales_area_sqm?: number | null;
  stockroom_area_sqm?: number | null;
  social_area_sqm?: number | null;
  other_area_sqm?: number | null;
  lease?: LeaseInfo | null;
  contacts?: ContactInfo[] | null;
  building_attributes?: BuildingAttributes | null;
}

export interface Pagination {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface BuildingListResponse {
  data: Building[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  pagination: Pagination;
}

// Weather Types
export interface WeatherData {
  timestamp: string;
  temperature_celsius?: number;
  humidity_percent?: number;
  wind_speed_ms?: number;
  wind_direction_degrees?: number;
}

export interface WeatherHistoryPoint {
  bucket: string;
  avg_temperature?: number;
  min_temperature?: number;
  max_temperature?: number;
  avg_humidity?: number;
  avg_wind_speed?: number;
  reading_count: number;
}

export interface WeatherHistoryResponse {
  building_id: string;
  building_name: string;
  start_date: string;
  end_date: string;
  aggregation: string;
  data: WeatherHistoryPoint[];
  total_points: number;
}

export interface DailyForecast {
  date: string;
  temperature_max?: number | null;
  temperature_min?: number | null;
  weather_code?: number | null;
  precipitation_probability?: number | null;
  wind_speed_max?: number | null;
}

export interface WeatherWithForecastResponse {
  building_id: string;
  building_name: string;
  latitude: number;
  longitude: number;
  current: WeatherData;
  current_weather_code?: number | null;
  current_feels_like?: number | null;
  forecast: DailyForecast[];
  source: string;
  fetched_at: string;
}

// Analytics Types
export type AggregationLevel = 'raw' | 'hourly' | 'daily' | 'weekly' | 'monthly';

/**
 * SEN-1441 — one energy-source slice of a headline energy-intensity figure.
 *
 * The headline `energy_intensity_kwh_sqm` spans every fuel a site meters; the
 * monthly retail report ranks stores one fuel at a time. Each slice is
 * annualised over its OWN `days_with_data`, so the per-source EUIs need not
 * add up to the headline — `total_kwh` always does.
 */
export interface EnergyIntensitySourceSlice {
  energy_source: string;
  total_kwh: number;
  eui_kwh_sqm_year: number | null;
  days_with_data: number;
  buildings_count: number;
}

export interface KPIData {
  /** NULL = unknown (no data / nothing priced), never 0: the reason is in `KPIResponse.unavailable`. */
  total_consumption_kwh: number | null;
  energy_intensity_kwh_sqm: number | null;
  /** Largest contributor first. Absent on responses from an older backend. */
  energy_intensity_by_source?: EnergyIntensitySourceSlice[];
  estimated_cost_eur?: number | null;
  estimated_carbon_kg?: number | null;
  period_days: number;
  buildings_count: number;
  total_area_sqm: number;
}

export interface KPIPeriodComparison {
  vs_last_month_percent?: number;
  vs_last_year_percent?: number;
  trend: 'up' | 'down' | 'stable';
}


export interface KPIResponse {
  building_ids: string[];
  start_date: string;
  end_date: string;
  /**
   * SEN-1441 — which metering universe produced these numbers.
   * false = all meters; true = automatically metered data sources only,
   * the monthly retail report's basis. Absent on an older backend.
   */
  metered_only?: boolean;
  kpis: KPIData;
  comparison?: KPIPeriodComparison;
  /** Why a KPI is NULL, by KPIData field (a refusal, nothing priced, no-data operands). Owner ruling 2026-10-21. */
  unavailable?: Record<string, string>;
  /** What a shown KPI leaves out (`factor_coverage_partial`), by KPIData field: one thin line under the value. */
  partial?: Record<string, string>;
  /** KPIData fields still being answered: a progressive (onPartial) response only; absent on the final one. */
  pending?: string[];
  calculated_at: string;
}

// Weather-adjusted heat consumption (graddage 17°C, TI 1991-2020 normal year)
export interface HeatAdjustedResponse {
  building_id: string;
  start_date: string;
  end_date: string;
  actual_heat_kwh: number;          // combined district heating + natural gas
  adjusted_heat_kwh: number;        // normalized to normal-year graddage
  graddage_17_actual: number;
  graddage_17_normal: number | null;
  graddage_ratio_pct: number | null;
  in_season_days: number;
  days_with_temperature_data: number;
  country_code: string | null;
  base_temperature_c: number;       // 17.0
  gate_threshold_c: number;         // 12.0
  normal_year_label: string;        // "1991-2020"
  normal_year_source: string;       // "Teknologisk Institut (graddage)"
  normal_year_reference_url?: string | null;  // deprecated: no longer populated (TI link is dead)
  energy_types_included: string[];
}

// Auth Types
export interface User {
  id: string;
  email: string;
  role: string;
  customer_id?: string;
  customer_name?: string;
  is_active: boolean;
  permissions: string[];
  hierarchy_level: number;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

// Constants
export const BUILDING_TYPES = [
  { value: 'office', label: 'Office' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'retail', label: 'Retail' },
  { value: 'residential', label: 'Residential' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'mixed_use', label: 'Mixed Use' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'other', label: 'Other' },
] as const;

export const ENERGY_SOURCES = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'natural_gas', label: 'Natural Gas' },
  { value: 'solar', label: 'Solar' },
  { value: 'district_heating', label: 'District Heating' },
  { value: 'oil', label: 'Oil' },
  { value: 'propane', label: 'Propane' },
  { value: 'biomass', label: 'Biomass' },
  { value: 'geothermal', label: 'Geothermal' },
  { value: 'wind', label: 'Wind' },
  { value: 'other', label: 'Other' },
] as const;

export const COUNTRIES = [
  // Nordic countries
  { value: 'DK', label: 'DK - Denmark' },
  { value: 'SE', label: 'SE - Sweden' },
  { value: 'NO', label: 'NO - Norway' },
  { value: 'FI', label: 'FI - Finland' },
  { value: 'IS', label: 'IS - Iceland' },
  // Western Europe
  { value: 'DE', label: 'DE - Germany' },
  { value: 'NL', label: 'NL - Netherlands' },
  { value: 'BE', label: 'BE - Belgium' },
  { value: 'LU', label: 'LU - Luxembourg' },
  { value: 'FR', label: 'FR - France' },
  { value: 'GB', label: 'GB - United Kingdom' },
  { value: 'IE', label: 'IE - Ireland' },
  { value: 'AT', label: 'AT - Austria' },
  { value: 'CH', label: 'CH - Switzerland' },
  // Southern Europe
  { value: 'ES', label: 'ES - Spain' },
  { value: 'PT', label: 'PT - Portugal' },
  { value: 'IT', label: 'IT - Italy' },
  { value: 'GR', label: 'GR - Greece' },
  { value: 'MT', label: 'MT - Malta' },
  { value: 'CY', label: 'CY - Cyprus' },
  // Central Europe
  { value: 'PL', label: 'PL - Poland' },
  { value: 'CZ', label: 'CZ - Czech Republic' },
  { value: 'SK', label: 'SK - Slovakia' },
  { value: 'HU', label: 'HU - Hungary' },
  { value: 'SI', label: 'SI - Slovenia' },
  // Balkans
  { value: 'HR', label: 'HR - Croatia' },
  { value: 'BA', label: 'BA - Bosnia and Herzegovina' },
  { value: 'RS', label: 'RS - Serbia' },
  { value: 'ME', label: 'ME - Montenegro' },
  { value: 'MK', label: 'MK - North Macedonia' },
  { value: 'AL', label: 'AL - Albania' },
  { value: 'XK', label: 'XK - Kosovo' },
  // Eastern Europe
  { value: 'BG', label: 'BG - Bulgaria' },
  { value: 'RO', label: 'RO - Romania' },
  { value: 'MD', label: 'MD - Moldova' },
  { value: 'UA', label: 'UA - Ukraine' },
  // Baltic states
  { value: 'EE', label: 'EE - Estonia' },
  { value: 'LV', label: 'LV - Latvia' },
  { value: 'LT', label: 'LT - Lithuania' },
  // Other
  { value: 'TR', label: 'TR - Turkey' },
] as const;

// Device Types
export interface Device {
  id: string;
  building_id: string;
  building_name?: string;
  shelly_device_id: string;
  device_type: string;
  device_name?: string;
  shelly_room?: string;
  model?: string;
  generation?: number;
  firmware_version?: string;
  is_online: boolean;
  last_seen_at?: string;
  power_watts?: number;
  energy_wh?: number;
  yesterday_kwh?: number;
  parent_device_id?: string | null;
  channel_index?: number | null;
  /** True when the device's channels are the L1/L2/L3 phases of ONE unit. */
  is_three_phase?: boolean;
  last_reading_at?: string;
  created_at: string;
  updated_at: string;
}

// JYSK Energy Management Types

export interface EnergySource {
  id: string;
  code: string;
  name: string;
  unit: string;
  kwh_conversion_factor: number | null;  // null for volume-only sources (water, m³)
  co2_factor_kg_per_kwh: number | null;
  color: string | null;
  icon: string | null;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface EnergySourceEmissionFactor {
  id: string;
  country_code: string;
  energy_source_id: string;
  energy_source_code: string;
  energy_source_name: string;
  co2_kg_per_kwh: number;
  valid_from: string;
  valid_to?: string;
  source?: string;
}

export interface SEUCategory {
  id: string;
  code: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
  display_order: number;
  created_at?: string;
}

export interface EnergyPrice {
  id: string;
  country_code: string;
  energy_source_id: string;
  energy_source_name?: string;
  price_per_kwh: number;
  currency: string;
  valid_from: string;
  valid_to?: string;
  source?: string;
  created_at: string;
  updated_at: string;
}

export interface EnergyPriceCreate {
  country_code: string;
  energy_source_id: string;
  price_per_kwh: number;
  currency?: string;
  valid_from: string;
  valid_to?: string;
  source?: string;
}

export type InitiativeType = 'LED' | 'HVAC' | 'closed_hour_load' | 'dh_summer_waste' | 'smart_store' | 'other';
export type InitiativeStatus = 'opportunity' | 'active' | 'completed' | 'verified' | 'cancelled' | 'superseded';

export interface SavingsInitiative {
  id: string;
  building_id: string;
  building_name?: string;
  name: string;
  description?: string;
  initiative_type: InitiativeType;
  status: InitiativeStatus;
  implementation_date?: string;
  completion_date?: string;
  roi_date?: string;
  estimated_annual_savings_kwh?: number;
  estimated_annual_savings_eur?: number;
  estimated_cost_eur?: number;
  actual_savings_kwh?: number;
  actual_savings_eur?: number;
  actual_cost_eur?: number;
  payback_months?: number;
  impact_percent?: number;  // Percentage impact: positive = savings (10 = -10%), negative = increase (-5 = +5%)
  // Canonical vocabulary — matches backend SavingsInitiativeCreate.energy_type (see March 2026 energy-type normalization)
  energy_type?: 'electricity_consumption' | 'heat_consumption' | 'natural_gas' | 'water_consumption' | null;
  originated_from?: 'discovery' | 'manual' | 'ad_hoc';
  savings_to_date_kwh?: number | null;
  savings_to_date_eur?: number | null;
  days_elapsed?: number | null;
  data_quality_warning?: string | null;
  notes?: string;
  owner_name?: string;  // Action owner name
  owner_email?: string; // Action owner email
  source?: string;
  // No longer shown, sorted or filtered on (2026-10-07); the API may still send them, as null.
  recommendation?: 'go' | 'consider' | 'skip' | null;
  recommendation_reason?: string | null;
  suitability_score?: number | null;
  evidence_doc?: string;
  analysis_data?: Record<string, unknown>;
  savings_confidence?: string;
  promoted_at?: string;
  realized_at?: string;
  // Realization fields (PR #158 SimpleRealizationsService)
  annualised_savings_kwh_interim?: number | null;
  annualised_savings_eur_interim?: number | null;
  final_annualised_savings_kwh?: number | null;
  final_annualised_savings_eur?: number | null;
  annualised_savings_locked_at?: string | null;
  // Lifecycle / approval fields (PR #156 state machine)
  approved_at?: string | null;
  approved_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SavingsInitiativeCreate {
  building_id: string;
  name: string;
  description?: string;
  initiative_type: InitiativeType;
  status?: InitiativeStatus;
  implementation_date?: string;
  completion_date?: string;
  estimated_annual_savings_kwh?: number;
  estimated_annual_savings_eur?: number;
  estimated_cost_eur?: number;
  actual_savings_kwh?: number;
  actual_savings_eur?: number;
  actual_cost_eur?: number;
  payback_months?: number;
  impact_percent?: number;  // Percentage impact: positive = savings, negative = increase
  // Canonical vocabulary — matches backend SavingsInitiativeCreate.energy_type (see March 2026 energy-type normalization)
  energy_type?: 'electricity_consumption' | 'heat_consumption' | 'natural_gas' | 'water_consumption' | null;
  originated_from?: 'discovery' | 'manual' | 'ad_hoc';
  notes?: string;
  owner_name?: string;  // Action owner name
  owner_email?: string; // Action owner email
}

/**
 * Lifecycle buckets on the Initiatives page.
 *
 * Canonical server-side semantics (see backend/app/routers/buildings.py):
 *   ARCHIVED := superseded_at IS NOT NULL OR status IN ('cancelled','superseded')
 *   LIVE     := superseded_at IS NULL  AND status NOT IN ('cancelled','superseded')
 */
export type InitiativeLifecycleBucket =
  | 'opportunities'
  | 'active'
  | 'realizations'
  | 'archive';

/** Row counts per bucket over the FULL filtered set — never page-derived. */
export interface InitiativeLifecycleCounts {
  opportunities: number;
  active: number;
  realizations: number;
  archive: number;
}

/** FO-10 (SEN-302): the same per-bucket counts as InitiativeLifecycleCounts, for one country —
 *  one row of GET /buildings/initiatives/batch?by_country=true's counts_by_country. */
export interface CountryInitiativeCounts extends InitiativeLifecycleCounts {
  country_code: string | null;
}

/** Headline KPI aggregates over the FULL requested bucket — never page-derived. */
export interface InitiativeAggregates {
  total_savings_eur: number;
  total_investment_eur: number;
  payback_months: number;
  building_count: number;
}

export const EMPTY_INITIATIVE_COUNTS: InitiativeLifecycleCounts = {
  opportunities: 0,
  active: 0,
  realizations: 0,
  archive: 0,
};

export const EMPTY_INITIATIVE_AGGREGATES: InitiativeAggregates = {
  total_savings_eur: 0,
  total_investment_eur: 0,
  payback_months: 0,
  building_count: 0,
};

// Constants for JYSK types
export const INITIATIVE_TYPES = [
  { value: 'LED', label: 'LED Lighting' },
  { value: 'HVAC', label: 'HVAC Schedule' },
  { value: 'closed_hour_load', label: 'Closed-Hour Load Reduction' },
  { value: 'dh_summer_waste', label: 'DH Summer Waste' },
  { value: 'smart_store', label: 'Smart Store' },
  { value: 'other', label: 'Other' },
] as const;

export const INITIATIVE_STATUSES = [
  { value: 'opportunity', label: 'Opportunity', color: 'cyan' },
  { value: 'active', label: 'Active', color: 'blue' },
  { value: 'completed', label: 'Completed', color: 'emerald' },
  { value: 'verified', label: 'Verified', color: 'teal' },
  { value: 'cancelled', label: 'Cancelled', color: 'slate' },
  { value: 'superseded', label: 'Superseded', color: 'zinc' },
] as const;

// Load Profile Analysis Types

// ============================================
// User Management Types
// ============================================

export interface Role {
  id: string;
  code: string;
  name: string;
  description?: string;
  permissions: string[];
  hierarchy_level: number;
}

export interface Permission {
  id: string;
  code: string;
  name: string;
  category: string;
  description?: string;
}

export interface UserListItem {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name: string;
  role_code: string;
  role_name: string;
  customer_id?: string;
  customer_name?: string;
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
  assigned_buildings_count: number;
  assigned_countries_count: number;
}

/** One entry of the assignee picker: a person (who may have no login). `id` is what a task's assignee_user_id holds. */
export interface AssigneeOption {
  id: string;
  display_name: string;
}

export interface UserDetail extends UserListItem {
  phone?: string;
  permissions: string[];
  assigned_buildings: {
    building_id: string;
    name: string;
    city: string;
    country_code: string;
    access_level: string;
  }[];
  assigned_countries: string[];
  updated_at?: string;
}

export interface UserCreate {
  email: string;
  password?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role_code: string;
  customer_id?: string;
  is_active?: boolean;
  building_ids?: string[];
  country_codes?: string[];
}

export interface UserUpdate {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role_code?: string;
  is_active?: boolean;
  building_ids?: string[];
  country_codes?: string[];
}

export interface UserInvite {
  email: string;
  first_name?: string;
  last_name?: string;
  role_code: string;
  building_ids?: string[];
  country_codes?: string[];
  message?: string;
}

export interface Invitation {
  id: string;
  email: string;
  role_code: string;
  customer_id?: string;
  invited_by: string;
  invited_by_name?: string;
  expires_at: string;
  accepted_at?: string;
  created_at: string;
}

// Role constants for UI

// ============================================
// Meter Points & Data Sources Types
// ============================================

export type DataSourceRole = 'primary' | 'backup' | 'validation';
export type MeterType = 'main' | 'sub_meter' | 'production';

export interface DataSourceSummary {
  id: string;
  adapter_id: string;
  source_system?: string;
  role: DataSourceRole;
  is_active: boolean;
  /**
   * Is this source fed BY HAND (or from a human-shaped artefact like an
   * invoice PDF) rather than by a machine? `data_sources.is_manual` is the
   * ONLY correct answer to the manual-vs-automatic question — the adapter
   * NAME is not. `manual`, `pdf_bulk_invoice` and `resource-advisor` are all
   * `is_manual: true`; `dk-eloverblik`, `se-elkraft`, `dk-eforsyning`,
   * `shelly-cloud`, `shelly-device` and `es-datadis` are all
   * `false`. Keying on the name meant a district-heating invoice read out of
   * a PDF counted as "automated", which blocked forecasting on ten Danish
   * meters whose every feed is manual.
   *
   * Optional only for back-compat with a response cached before the field
   * shipped; every read must treat a missing value as the fail-safe answer
   * for that call site.
   */
  is_manual?: boolean;
  /** Stored column, written only by the adapter ingestion path. Do not use
   *  for freshness: manual / bulk writers never update it. */
  last_reading_at?: string;
  /** End of the newest covered hour, from the daily aggregate (bulk
   *  /meter-points only, active sources only). null = no reading in 400 days. */
  latest_reading_at?: string | null;
  /** `data_sources.config->>'deactivated_reason'` when the source is
   *  deactivated (`no_authorization`, `connection_deleted`,
   *  `closed_at_provider`, ...); null while active or never explicitly
   *  deactivated. */
  deactivated_reason?: string | null;
  /** True when a deactivated source's reason means it comes back on its own
   *  at the next discovery (a lapsed provider authorization, or a deleted
   *  connection that kept its meters) — see
   *  `app.connectors.registry.REOFFERED_REASONS`. Never show a plain
   *  "Connect" action for a source with this set; it is not a never-linked
   *  meter. */
  is_reofferable?: boolean;
  /** The connection (`ConnectionOut.id` on `/connectors`) that owns this
   *  source via `data_sources.identity_id`, when known — the same id a
   *  `UnifiedConnection` carries. Lets the Integrations page attribute a
   *  parked (reofferable) source to its connector row instead of showing
   *  it only in the generic unlinked-meter-points list. Null for manual/PDF
   *  sources or sources with no identity. */
  connection_id?: string | null;
}

/**
 * Data freshness of one meter, classified by the BACKEND with one rule
 * (backend/app/services/meter_freshness.py): `fresh` when the newest actual
 * reading is within one and a half reading periods (its frequency) plus the
 * provider's publication delay, `stale` when overdue, `no_data` with no actual reading.
 * The frontend only counts these
 * statuses; it holds no adapter names or thresholds.
 */
export type MeterFreshnessStatus =
  | 'fresh'
  | 'stale'
  | 'no_data';

export type FreshnessCadence = 'hourly_next_day' | 'daily_lagged' | 'intraday' | 'monthly' | 'none';

export interface MeterFreshness {
  status: MeterFreshnessStatus;
  latest_reading_at?: string | null;
  adapter_id?: string | null;
  cadence?: FreshnessCadence | null;
  /** Reading frequency of the deciding source, from its own actual readings. */
  frequency?: FreshnessFrequency | null;
  /** Owner ruling 2026-10-20 (NomOS POST /readings/latest-actual): when the meter's latest ACTUAL reading
   *  ends (estimates, model values and forecast months never count), and the whole days since then on the
   *  tenant's calendar. null = never read; absent = not known (the read did not answer). */
  latest_actual_at?: string | null;
  days_since_actual?: number | null;
}

export type FreshnessFrequency = '15min' | 'hourly' | 'daily' | 'monthly' | 'quarterly' | 'annual';

/** One rule (backend meter_freshness): overdue when the newest actual reading is older than one reading
 *  period plus the provider's publication delay. */
export interface FreshnessBudget {
  cadence: FreshnessCadence;
  expected_latency: string;
  publication_delay_hours: number;
}

export interface MeterPoint {
  id: string;
  building_id: string;
  name: string;
  meter_type: MeterType;
  energy_source?: string | null;
  is_main_meter: boolean;
  rolls_up_to?: string;
  display_order: number;
  is_active: boolean;
  data_sources: DataSourceSummary[];
  // SEU category
  seu_category_id?: string | null;
  seu_category_code?: string | null;
  seu_category_name?: string | null;
  // Editable from the hierarchical meters table (2026-05-30).
  // Backend surfaces these via PUT /meter-points/{id} response + bulk endpoint.
  energy_source_id?: string;
  parent_meter_id?: string | null;
  // Device relationship
  device_id?: string;
  channel_id?: string;
  device_name?: string;
  device_type?: string;
  device_is_online?: boolean;
  // Active date range
  active_from?: string | null;  // "YYYY-MM-DD"
  active_to?: string | null;    // "YYYY-MM-DD", null = ongoing
  /** Nightly job fills gaps inside this meter's active window (migration 222). */
  forecast_gaps: boolean;
  /** Bulk /meter-points only: freshness against the adapter's budget. */
  freshness?: MeterFreshness | null;
  created_at: string;
  updated_at: string;
}

export interface MeterPointCreate {
  name: string;
  /** Required by the API (energy_sources.id, see GET /energy-sources). */
  energy_source_id: string;
  parent_meter_id?: string | null;
  meter_type?: MeterType;
  is_main_meter?: boolean;
  rolls_up_to?: string;
  display_order?: number;
  is_active?: boolean;
  active_from?: string | null;
  active_to?: string | null;
  seu_category_id?: string | null;
}

/**
 * An open meter of the same site x energy source that a newly linked meter
 * takes over from (backend app/services/meter_supersedes.py). The user is
 * asked to set its end date (active_to) so it stops counting.
 */
export interface SupersedeCandidate {
  meter_id: string;
  name: string;
  last_reading_day: string | null;    // "YYYY-MM-DD"
  suggested_end_date: string | null;  // "YYYY-MM-DD" — day before the new source's first data
}

/** Link endpoints return the meter plus the meters it takes over from. */
export interface MeterLinkResult {
  message: string;
  meter_point_id: string;
  supersedes_candidates?: SupersedeCandidate[];
}

export interface MeterPointUpdate extends Partial<MeterPointCreate> {
  // Fields added for the inline-editable hierarchical meters table (2026-05-30).
  // Backend MeterUpdate schema supports these via PUT /meter-points/{id}.
  energy_source_id?: string;
  parent_meter_id?: string | null;
  /** Manual meters only this release — see MeterListTable's isManualOnly gate. */
  forecast_gaps?: boolean;
}

// EnergySource is already defined earlier in this file (line ~836) for the
// settings / emission-factors flow; we reuse it for the meters table picker.

export interface SeuCategory {
  id: string;
  code: string;
  name: string;
  color: string | null;
  icon: string | null;
  display_order: number;
}

export interface MeterPointListResponse {
  data: MeterPoint[];
  total: number;
}

// Bulk meter point with building info (for portfolio-wide meter views)
export interface MeterPointWithBuilding extends MeterPoint {
  building_name: string;
}

export interface BulkMeterPointListResponse {
  data: MeterPointWithBuilding[];
  total: number;
  /** adapter_id -> budget the freshness statuses were computed with. */
  freshness_budgets?: Record<string, FreshnessBudget>;
}

export interface DataSource {
  id: string;
  meter_point_id: string;
  adapter_id: string;
  source_system?: string;
  external_id?: string;
  device_id?: string;
  role: DataSourceRole;
  priority: number;
  config: Record<string, unknown>;
  is_active: boolean;
  last_reading_at?: string;
  last_error?: string;
  device_name?: string;
  device_model?: string;
  device_is_online?: boolean;
  created_at: string;
  updated_at: string;
}

// Meter Point constants

// ============================================
// Consumption Breakdown Types
// ============================================

export interface DeviceConsumption {
  device_id: string;
  device_name: string;
  seu_category?: string;
  seu_category_name?: string;
  total_kwh: number;
  percentage_of_total: number;
  avg_kw?: number;
  reading_count: number;
}

export interface MeterPointConsumption {
  meter_point_id: string;
  meter_point_name: string;
  meter_type: string;
  energy_source?: string | null;
  total_kwh: number;
  percentage_of_total: number;
  parent_meter_id?: string | null;
  percentage_of_parent?: number;
  data_source?: string;
  quality: string;
  reading_count: number;
}

export interface BuildingConsumptionBreakdown {
  building_id: string;
  building_name: string;
  start_date: string;
  end_date: string;
  total_kwh: number;
  data_source_type: 'meter_point' | 'device_aggregation' | 'none';
  meter_points: MeterPointConsumption[];
  devices: DeviceConsumption[];
  devices_total_kwh: number;
  device_coverage_percent?: number;
  calculated_at: string;
}

export interface ConsumptionBreakdownResponse {
  start_date: string;
  end_date: string;
  buildings: BuildingConsumptionBreakdown[];
  portfolio_total_kwh: number;
  calculated_at: string;
}

// System Settings

// ============================================================================
// Climate Zone Types
// ============================================================================

export interface ClimateZone {
  code: string;
  name: string;
  description: string | null;
  avg_temp_summer_c: number | null;
  avg_temp_winter_c: number | null;
  heating_degree_days: number | null;
  cooling_degree_days: number | null;
  heating_factor: number;
  cooling_factor: number;
  total_hvac_factor: number;
  data_source: string | null;
}

export interface EnergyBenchmark {
  climate_zone_code: string;
  building_type: string;
  kwh_per_sqm_total: number | null;
  kwh_per_sqm_heating: number | null;
  kwh_per_sqm_cooling: number | null;
  kwh_per_sqm_lighting: number | null;
  kwh_per_sqm_equipment: number | null;
  data_source: string;
  data_year: number | null;
  region_applicability: string | null;
}

export interface GridEmissionFactor {
  country_code: string;
  country_name: string;
  grid_region: string | null;
  year: number;
  co2_kg_per_kwh: number;
  scope2_location_based: number | null;
  scope2_market_based: number | null;
  renewable_pct: number | null;
  data_source: string;
}

export interface ClimateDataSummary {
  climate_zones_count: number;
  climate_regions_count: number;
  energy_benchmarks_count: number;
  emission_factors_count: number;
  countries_with_emissions: number;
  latest_emission_year: number | null;
  data_sources: string[];
  last_updated: string | null;
}

// Planned Building / Consumption Estimate types
export interface ConsumptionEstimateRequest {
  building_type: string;
  size_sqm: number;
  country_code: string;
  latitude?: number | null;
  longitude?: number | null;
  climate_zone?: string | null;
}

export interface ConsumptionEstimate {
  annual_kwh: number;
  annual_co2_kg: number;
  kwh_per_sqm: number;
  estimation_method: string;
  confidence: 'high' | 'medium' | 'low';
  similar_buildings_count: number;
  climate_zone?: string | null;  // e.g., 'Cfb'
  climate_zone_name?: string | null;  // e.g., 'Temperate Oceanic'
  notes?: string | null;
  // Energy breakdown from Master Data (if available)
  kwh_per_sqm_heating?: number | null;
  kwh_per_sqm_cooling?: number | null;
  kwh_per_sqm_lighting?: number | null;
  kwh_per_sqm_equipment?: number | null;
  data_source?: string | null;  // e.g., 'ASHRAE 90.1', 'EU TABULA'
  data_year?: number | null;
}

// ============================================
// Proposal Types (World Model)
// ============================================

export * from './proposal';

// Enhanced Dashboard Types




// =============================================================================
// INTEGRATIONS TYPES
// =============================================================================

export type ConnectionStatus = 'pending' | 'active' | 'error';

// SEN 2026-07-30 reintegration: the durable Shelly credential is the account
// email+password (see cloud_auth.py on the backend) — the auth_key is
// derived automatically and is never round-tripped to the frontend.
export interface ShellyCredentialsConnectionCreate {
  email: string;
  password: string;
  account_name?: string;
}

export interface ShellyConnection {
  id: string;
  customer_id: string;
  account_name?: string | null;
  server_url: string;
  status: ConnectionStatus;
  last_validated_at?: string | null;
  last_sync_at?: string | null;
  last_error?: string | null;
  total_device_count: number;
  online_device_count: number;
  linked_building_count: number;
  linked_building_names?: string[];
  created_at: string;
  updated_at: string;
}

// Shelly units (spec 2026-09-16 §4/§5)
export interface ShellyUnit {
  external_id: string;
  device_id: string;
  channel: number;
  name: string;
  device_type: string;
  model?: string | null;
  generation?: number | null;
  room_id?: string | null;
  room?: string | null;
  is_online: boolean;
  is_three_phase: boolean;
  proposed_seu?: string | null;
  suggested_trunk: boolean;
  suggested_trunk_of?: string | null;
  meter_id?: string | null;
  building_id?: string | null;
  seu_code?: string | null;
  is_trunk?: boolean | null;
  trunk_external_id?: string | null;
  parent_meter_id?: string | null;
}

export interface ShellyUnitsResponse {
  units: ShellyUnit[];
  total_count: number;
  online_count: number;
}

export interface ShellyUnitDecision {
  external_id: string;
  device_id: string;
  channel: number;
  name: string;
  device_type: string;
  model?: string | null;
  generation?: number | null;
  room?: string | null;
  is_online: boolean;
  is_three_phase: boolean;
  building_id: string;
  seu_code?: string | null;
  is_trunk: boolean;
  trunk_external_id?: string | null;
  parent_meter_id?: string | null;
}

export interface ShellyLinkUnitsRequest {
  units: ShellyUnitDecision[];
}

export interface ShellyLinkUnitsResponse {
  created: number;
  updated: number;
  deactivated: number;
  buildings: number;
}

// Shelly sync job (SyncStep — mirrors ElkraftSyncJob, per meter not per building)
export type ShellySyncStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface ShellySyncRequest {
  start_date?: string | null;
  end_date?: string | null;
}

export interface ShellySyncJob {
  id: string;
  connection_id: string;
  job_type: string;
  status: ShellySyncStatus;
  start_date: string;
  end_date: string;
  meters_processed: number;
  total_meters: number;
  records_inserted: number;
  progress_percent: number;
  current_meter_name?: string | null;
  errors: Array<{
    error: string;
    [key: string]: unknown;
  }>;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
}

// ============================================
// Google Places API Types
// ============================================

export interface PlacePrediction {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
}

export interface PlacesAutocompleteResponse {
  predictions: PlacePrediction[];
  configured: boolean;
}

export interface PlaceDetailsResponse {
  place_id: string;
  name: string;
  formatted_address: string;
  latitude: number;
  longitude: number;
  street_number: string;
  route: string;
  locality: string;
  administrative_area: string;
  postal_code: string;
  country: string;
  country_code: string;
  opening_hours?: OpeningHours | null;
  weekday_text?: string[] | null;
}

// ============================================
// Elkraft Integration Types (Swedish Energy via Kiona)
// ============================================

export type ElkraftConnectionStatus = 'pending' | 'active' | 'error';
export type ElkraftSyncStatus = 'pending' | 'running' | 'completed' | 'failed';
export type ElkraftNodeType = 'top' | 'folder' | 'building' | 'eb' | 'meter';

export interface ElkraftValidateRequest {
  username?: string;
  password?: string;
  session_token?: string;
}

export interface ElkraftValidateResponse {
  valid: boolean;
  top_id?: number | null;
  top_name?: string | null;
  building_count: number;
  data_available_from?: string | null;
  error?: string | null;
}

export interface ElkraftConnectionCreate {
  username?: string;
  password?: string;
  session_token?: string;
  connection_name?: string | null;
}

export interface ElkraftConnection {
  id: string;
  customer_id: string;
  connection_name?: string | null;
  kiona_username: string;
  top_id?: number | null;
  top_name?: string | null;
  data_available_from?: string | null;
  status: ElkraftConnectionStatus;
  building_count: number;
  last_sync_at?: string | null;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ElkraftConnectionListResponse {
  connections: ElkraftConnection[];
  total_count: number;
}

export interface ElkraftHierarchyNode {
  id?: string | null;
  elkraft_id: number;
  elkraft_parent_id?: number | null;
  node_type: ElkraftNodeType;
  name: string;
  path?: string | null;
  depth: number;
  street_address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  linked_building_id?: string | null;
  linked_building_name?: string | null;
  children: ElkraftHierarchyNode[];
}

export interface ElkraftDiscoverResponse {
  hierarchy: ElkraftHierarchyNode[];
  total_nodes: number;
  building_count: number;
  data_available_from?: string | null;
}

export interface ElkraftLinkRequest {
  elkraft_building_id: number;
  building_id: string;
  is_main_meter?: boolean;
}

export interface ElkraftBulkLinkRequest {
  links: ElkraftLinkRequest[];
}

/** Mirrors `ElkraftIntegrationService.bulk_link_buildings`. Returns 200 even
 *  when buildings fail to link — check `skipped_count` / `error_details`. */
export interface ElkraftBulkLinkResponse {
  linked_count: number;
  skipped_count: number;
  error_details: { elkraft_building_id: number; error: string }[];
}

export interface ElkraftSyncRequest {
  start_date?: string | null;
  end_date?: string | null;
}

export interface ElkraftSyncJob {
  id: string;
  connection_id: string;
  job_type: string;
  status: ElkraftSyncStatus;
  start_date: string;
  end_date: string;
  buildings_processed: number;
  total_buildings: number;
  records_inserted: number;
  progress_percent: number;
  current_building_name?: string | null;
  errors: Array<{
    elkraft_building_id?: number | null;
    building_name?: string | null;
    error: string;
    timestamp: string;
  }>;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
}

// ============================================
// DataDis Integration Types (Spanish Electricity)
// ============================================

export type DatadisConnectionStatus = 'pending' | 'active' | 'error';
export type DatadisSyncStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface DatadisConnection {
  id: string;
  customer_id: string;
  connection_name?: string | null;
  username?: string | null;
  authorized_nif?: string | null;
  status: DatadisConnectionStatus;
  cups_count: number;
  last_sync_at?: string | null;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DatadisCUPS {
  cups: string;  // 22-char CUPS identifier (ES0021000012345678JL)
  distributor_code: string;
  distributor?: string | null;
  point_type: number;
  address?: string | null;
  municipality?: string | null;
  province?: string | null;
  postal_code?: string | null;
  valid_date_from?: string | null;  // Contract start date (YYYY/MM) from DataDis
  valid_date_to?: string | null;
  // Reconciliation match status
  match_status?: string | null;  // "linked", "matched", "new"
  matched_meter_point_id?: string | null;
  matched_meter_point_name?: string | null;
  // Linked building info
  linked_building_id?: string | null;
  linked_building_name?: string | null;
  // Address-based building suggestion
  suggested_building_id?: string | null;
  suggested_building_name?: string | null;
  suggested_match_method?: string | null;
  // Shared site matcher score (100..50); >= PRESELECT_THRESHOLD is preselected
  suggested_match_score?: number | null;
}

export interface DatadisValidateRequest {
  username: string;
  password: string;
}

export interface DatadisValidateResponse {
  valid: boolean;
  cups_count: number;
  error?: string | null;
}

export interface DatadisConnectionCreate {
  username: string;
  password: string;
  authorized_nif?: string | null;
  connection_name?: string | null;
  cups_count?: number;
}

export interface DatadisConnectionResponse extends DatadisConnection {
  cups_list: DatadisCUPS[];
}

export interface DatadisConnectionListResponse {
  connections: DatadisConnectionResponse[];
  total_count: number;
}

export interface DatadisDiscoverResponse {
  cups_list: DatadisCUPS[];
  total_count: number;
  linked_count: number;
  matched_count: number;
  new_count: number;
}

export interface DatadisLinkRequest {
  cups: string;
  building_id: string;
  cups_name?: string | null;
  match_status?: string | null;
  matched_meter_point_id?: string | null;
  address?: string | null;
  municipality?: string | null;
  province?: string | null;
  distributor_code?: string | null;
  point_type?: number | null;
  valid_date_from?: string | null;
  is_main_meter?: boolean;
}

export interface DatadisBulkLinkRequest {
  links: DatadisLinkRequest[];
}

/** Mirrors `bulk_link_datadis_cups` in backend/app/routers/integrations.py.
 *  Returns 200 even when links fail — check `errors`. */
export interface DatadisBulkLinkResponse {
  linked: number;
  errors: number;
  results: { cups: string; meter_point_id: string; status: string }[];
  error_details: { cups: string; error: string }[];
  supersedes_candidates?: SupersedeCandidate[];
}

export interface DatadisSyncRequest {
  start_date?: string | null;
  end_date?: string | null;
}

export interface DatadisSyncJob {
  id: string;
  connection_id: string;
  status: DatadisSyncStatus;
  start_date: string;
  end_date: string;
  cups_processed: number;
  total_cups: number;
  records_inserted: number;
  progress_percent: number;
  current_cups?: string | null;
  errors: Array<{
    cups?: string | null;
    error: string;
    timestamp: string;
  }>;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
}

// eForsyning Integration Types (Danish District Heating)
// =====================================================
export type EforsyningConnectionStatus = 'pending' | 'active' | 'error' | 'inactive';
export type EforsyningSyncStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface EforsyningConnection {
  id: string;
  customer_id: string;
  connection_name?: string | null;
  supplier_slug: string;
  consumer_number: string;
  allocation_pct: number;
  status: EforsyningConnectionStatus;
  meter_count: number;
  last_sync_at?: string | null;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Where a first-data date came from (backend app/services/first_data_probe.py):
 * the utility states it, it was found in the data, or neither answered and it
 * is the previous financial-year start.
 */
export type FirstDataSource = 'provider' | 'probe' | 'probe_partial' | 'fy_floor';

export interface EforsyningMeter {
  consumer_number: string;
  /** DFF installation number (`inr`). One consumer login can own several. */
  installation_id: string;
  connection_name?: string | null;
  supplier_slug: string;
  allocation_pct: number;
  /** What DFF answered for the billing year it probed (the current one when it has data). */
  lines?: number | null;
  first_line_date?: string | null;
  last_line_date?: string | null;
  /** ENG1 unit as DFF writes it, e.g. "MWh", "GJ". */
  unit?: string | null;
  has_m3?: boolean | null;
  meter_number?: string | null;
  installed_on?: string | null;
  /** First-data date (YYYY-MM-DD) and where it came from. */
  first_data_date?: string | null;
  first_data_source?: FirstDataSource | null;
  match_status?: 'linked' | 'new' | 'closed' | null;
  linked_building_id?: string | null;
  linked_building_name?: string | null;
  /** Site matcher suggestion (store code in the login label, or the utility's town). */
  suggested_building_id?: string | null;
  suggested_building_name?: string | null;
  suggested_match_method?: string | null;
  /** >= PRESELECT_THRESHOLD (lib/siteMatch.ts) is preselected; below is a suggestion only. */
  suggested_match_score?: number | null;
}

export interface EforsyningUtilityInfo {
  slug: string;
  label: string;
  api_server_url?: string | null;
  eforsyning_id?: number | null;
}

export interface EforsyningUtilitiesResponse {
  utilities: EforsyningUtilityInfo[];
  total_count: number;
}

export interface EforsyningValidateResponse {
  valid: boolean;
  api_server_url?: string | null;
  error?: string | null;
}

export interface EforsyningConnectionCreate {
  supplier_slug: string;
  consumer_number: string;
  pin_code: string;
  connection_name?: string | null;
  allocation_pct?: number;
  asset_id?: string;
  installation_id?: string;
  api_server_url?: string | null;
}

export interface EforsyningConnectionResponse extends EforsyningConnection {
  meters: EforsyningMeter[];
}

export interface EforsyningConnectionListResponse {
  connections: EforsyningConnectionResponse[];
  total_count: number;
}

export interface EforsyningDiscoverResponse {
  meters: EforsyningMeter[];
  total_count: number;
  linked_count: number;
  new_count: number;
}

export interface EforsyningLinkRequest {
  building_id: string;
  /** DFF installation number (`inr`) being linked. */
  installation_id: string;
  meter_name?: string | null;
  is_main_meter?: boolean;
  /** First-data date discovery showed; the source is required with it. */
  first_consumption_date?: string | null;
  first_consumption_source?: FirstDataSource | null;
}

export interface EforsyningSyncRequest {
  start_date?: string | null;
  end_date?: string | null;
}

export interface EforsyningSyncJob {
  id: string;
  connection_id: string;
  status: EforsyningSyncStatus;
  start_date: string;
  end_date: string;
  meters_processed: number;
  total_meters: number;
  records_inserted: number;
  progress_percent: number;
  current_meter?: string | null;
  errors: Array<{
    error: string;
  }>;
  started_at?: string | null;
  completed_at?: string | null;
}

// KMD MinForsyning types
export type KmdConnectionStatus = 'pending' | 'active' | 'error' | 'inactive';
export type KmdSyncStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface KmdPlantInfo {
  plant_number: number;
  utility_number: number;
  name: string;
  city?: string | null;
}

export interface KmdPlantsResponse {
  plants: KmdPlantInfo[];
}

export interface KmdConnection {
  id: string;
  customer_id: string;
  connection_name?: string | null;
  plant_number: number;
  utility_number: number;
  email: string;
  status: KmdConnectionStatus;
  meter_count: number;
  last_sync_at?: string | null;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
}

export interface KmdMeter {
  external_id: string;
  meter_number?: string | null;
  installation_number?: string | null;
  address?: string | null;
  street?: string | null;
  city?: string | null;
  postal_code?: string | null;
  energy_type?: string | null;
  first_consumption_date?: string | null;
  match_status?: 'linked' | 'new' | null;
  linked_building_id?: string | null;
  linked_building_name?: string | null;
}

export interface KmdValidateRequest {
  plant_number: number;
  utility_number: number;
  email: string;
  password: string;
}

export interface KmdValidateResponse {
  valid: boolean;
  meter_count: number;
  error?: string | null;
}

export interface KmdConnectionCreate {
  plant_number: number;
  utility_number: number;
  email: string;
  password: string;
  connection_name?: string | null;
}

export interface KmdConnectionResponse extends KmdConnection {
  meters: KmdMeter[];
}

export interface KmdConnectionListResponse {
  connections: KmdConnectionResponse[];
  total_count: number;
}

export interface KmdDiscoverResponse {
  meters: KmdMeter[];
  total_count: number;
  linked_count: number;
  new_count: number;
}

export interface KmdLinkRequest {
  building_id: string;
  external_id: string;
  meter_name?: string | null;
  energy_type?: string | null;
  first_consumption_date?: string | null;
}

export interface KmdSyncRequest {
  start_date?: string | null;
  end_date?: string | null;
}

export interface KmdSyncJob {
  id: string;
  connection_id: string;
  status: KmdSyncStatus;
  start_date?: string | null;
  end_date?: string | null;
  meters_processed: number;
  total_meters: number;
  records_inserted: number;
  progress_percent: number;
  current_meter?: string | null;
  errors: Array<{
    error: string;
  }>;
  started_at?: string | null;
  completed_at?: string | null;
}

// Customer Settings
export interface CustomerSettings {
  fiscal_year_start_month: number; // 1-12
}

// Fiscal Year (pre-calculated by API)
export interface FiscalYear {
  fiscal_year: number;
  start_date: string;
  end_date: string;
  label: string;
}

// ============================================
// Time Period Selector Types
// ============================================

export interface TimePeriodValue {
  periodKey: string;
  start: Date;
  end: Date;
}

// ============================================
// Diagnostics Types
// ============================================

export interface BusinessCase {
  action_id?: string;
  description: string;
  investment: { low: number; high: number; average: number };
  annual_savings: { low: number; high: number; average: number };
  payback_years: { low: number; high: number; average: number };
  npv: number;
  roi_pct: number;
  recommendation: string;
  confidence: string;
  effort: string;
}

export interface Recommendation {
  priority: number;
  symptom: string;
  diagnosis: string;
  action: {
    title: string;
    description?: string;
    steps: string[];
    effort: string;
  };
  business_case?: BusinessCase;
  expected_savings_pct?: [number, number];
}

// ============================================
// Solar Types
// ============================================
export * from './solar';

// ============================================
// Proposal Types
// ============================================
export interface Proposal {
  id: string;
  building_id: string;
  building_name: string;
  title: string;
  proposal_type: 'optimization' | 'anomaly_fix' | 'efficiency_improvement' | 'cost_reduction';
  description: string;
  estimated_savings_kwh: number;
  estimated_savings_eur: number;
  estimated_cost_eur: number | null;
  payback_months: number | null;
  confidence: 'high' | 'medium' | 'low';
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'approved' | 'rejected' | 'executed' | 'verified';
  created_at: string;
  auto_generated: boolean;
}

// ============================================
// Scheduled Reading Requests Types — SEN-461 archived the feature
// (mig 169 + commit 09758c7). All related types removed.
// ============================================

// ============================================
// Asset Types (FO-3b)
// ============================================

export type AssetProvenance = 'declared' | 'predicted' | 'confirmed'
export type AssetSource =
  | 'fixture_sheet'
  | 'meter_inference'
  | 'rule'
  | 'load_shape'
  | 'manual'
  | 'visit'
  | 'import'
  | 'register'
  | 'classifier'

export interface AssetMeasuredByEntry {
  meter_id: string
  meter_name: string
  attributed_pct: number
}

export interface AssetMeasuredAsset {
  asset_id: string
  asset_name: string
  attributed_pct: number
}

export interface AssetMeterEntry {
  meter_id: string
  meter_name: string
  measures: AssetMeasuredAsset[]
}

export interface Asset {
  id: string
  building_id: string
  name: string
  building_name?: string | null
  category: string
  category_id?: string | null
  category_name?: string | null
  seu_category_id?: string | null
  seu_category_name?: string | null
  device_id?: string | null
  meter_point_id?: string | null
  measured_by: AssetMeasuredByEntry[]
  meters: AssetMeterEntry[]
  manufacturer?: string | null
  model?: string | null
  serial_number?: string | null
  install_date?: string | null
  decommission_date?: string | null
  expected_lifespan_years?: number | null
  last_maintenance_date?: string | null
  next_maintenance_due?: string | null
  status: string
  controllable: boolean
  rated_power_kw?: number | null
  metadata?: Record<string, unknown> | null
  created_at: string
  updated_at: string
  // Inference / provenance fields (server-managed)
  provenance: AssetProvenance
  source?: AssetSource | null
  confidence?: number | null                   // 0-1, a prediction's probability (classifier or load shape); null otherwise
  basis?: string | null
  confirmed_by?: { kind: string; id: string } | null
  operating_schedule?: string | null
  category_code?: string | null
  quantity?: number | null
  zone?: string | null
  energy_source?: string | null
  energy_source_is_default?: boolean
  end_use?: string | null
  control_type?: string | null
  responsible_party?: string | null
  attributes?: Record<string, unknown>
  derived?: Record<string, number | null>
  confirmed_at?: string | null
  evidence_document_id?: string | null
  evidence_doc?: string | null
  measured_annual_kwh?: number | null
  measured_load_kw?: number | null
}

export type AssetFieldValue = string | number | boolean | null
export type AssetFieldValues = Record<string, AssetFieldValue>   // common keys + `attributes`

export interface AssetFieldDefinition {
  key: string
  label: string
  label_da: string
  type: 'number' | 'int' | 'bool' | 'enum' | 'text' | 'energy_source'
  why: string
  unit?: string | null
  enum: string[]
  min?: number | null
  max?: number | null
  priority: 'must' | 'later'
}

export interface AssetCategoryFields {
  code: string
  energy_source?: string | null
  end_use?: string | null
  fields: string[]
}

export interface AssetFieldDefinitions {
  common: AssetFieldDefinition[]
  categories: AssetCategoryFields[]
  fields: AssetFieldDefinition[]
  gwp: Record<string, number>
  gwp_basis: string
  derived: string[]
}

export interface AssetImportProblem {
  row: number
  problem: string
}

export interface AssetImportResult {
  answer: string
  rows: number
  created: number
  updated: number
  unchanged: number
  kept: number
  problems_total: number
  problems: AssetImportProblem[]
  dry_run: boolean
}

export interface AssetCreate {
  building_id: string
  name: string
  category_id?: string | null
  seu_category_id?: string | null
  device_id?: string | null
  meter_point_id?: string | null
  manufacturer?: string | null
  model?: string | null
  serial_number?: string | null
  install_date?: string | null
  decommission_date?: string | null
  expected_lifespan_years?: number | null
  last_maintenance_date?: string | null
  next_maintenance_due?: string | null
  status?: string
  controllable?: boolean
  rated_power_kw?: number | null
  metadata?: Record<string, unknown> | null
  quantity?: number | null
  zone?: string | null
  energy_source?: string | null
  operating_schedule?: string | null
  control_type?: string | null
  responsible_party?: string | null
  attributes?: Record<string, AssetFieldValue>
}

export interface AssetUpdate {
  name?: string
  category_id?: string | null
  seu_category_id?: string | null
  device_id?: string | null
  meter_point_id?: string | null
  manufacturer?: string | null
  model?: string | null
  serial_number?: string | null
  install_date?: string | null
  decommission_date?: string | null
  expected_lifespan_years?: number | null
  last_maintenance_date?: string | null
  next_maintenance_due?: string | null
  status?: string
  controllable?: boolean
  rated_power_kw?: number | null
  metadata?: Record<string, unknown> | null
  quantity?: number | null
  zone?: string | null
  energy_source?: string | null
  operating_schedule?: string | null
  control_type?: string | null
  responsible_party?: string | null
  attributes?: Record<string, AssetFieldValue>
}

export interface AssetType {
  id: string
  code?: string | null
  category: string
  name: string
  variant?: string | null
  rated_power_kw?: number | null
  capacity?: string | null
  energy_role: string
  seasonal_pattern?: string | null
}

export interface AssetListResponse {
  data: Asset[]
  total: number
  building_id?: string | null
  bacs?: unknown
  next_cursor?: string | null
}

/** GET /assets params (the list-query standard, FO-3b). */
export interface AssetQuery {
  building_id?: string
  role?: 'consuming' | 'metering'
  sort?: string
  order?: 'asc' | 'desc'
  filter?: string[]          // <field>:<op>:<value>
  group_by?: string[]
  has_meter?: boolean
  limit?: number
  offset?: number
}

/** A group_by answer of a list route. */
export interface GroupCount {
  values: Record<string, string | number | boolean | null>
  count: number
}

export interface GroupCounts {
  group_by: string[]
  groups: GroupCount[]
  total: number
}

export const ASSET_STATUSES = [
  { value: 'operational', label: 'Operational', color: 'emerald' },
  { value: 'degraded', label: 'Degraded', color: 'amber' },
  { value: 'failed', label: 'Failed', color: 'red' },
  { value: 'maintenance', label: 'Maintenance', color: 'blue' },
  { value: 'decommissioned', label: 'Decommissioned', color: 'slate' },
] as const

// ============================================
// Physical Task Types (FO-4: /api/v1/physical-tasks)
// Mirrors backend/app/schemas/physical_task.py::PhysicalTaskResponse field-for-field.
// ============================================

/** Legacy task_type values (backend LEGACY_TASK_TYPES). */
export type PhysicalTaskLegacyType =
  | 'inspection' | 'audit' | 'measurement' | 'repair' | 'maintenance' | 'calibration'
  | 'installation' | 'replacement' | 'upgrade' | 'cleaning' | 'adjustment'
  | 'documentation' | 'training'

/** Field-ops task_type values (backend FIELD_OPS_TASK_TYPES, FO-4). */
export type PhysicalTaskFieldOpsType = 'visit' | 'intervention_step' | 'data_fix' | 'follow_up'

export type PhysicalTaskType = PhysicalTaskLegacyType | PhysicalTaskFieldOpsType

/** Legacy statuses (backend LEGACY_STATUSES) + field-ops statuses (backend STATUS_BY_TYPE). */
export type PhysicalTaskStatus =
  | 'pending' | 'assigned' | 'in_progress' | 'completed' | 'verified' | 'cancelled'
  | 'backlog' | 'planned' | 'done' | 'signed'

export interface PhysicalTask {
  id: string
  building_id: string
  /** The site's name as the task record carries it (its site reference), so a list or form can label the task's
   *  building without the full building list. */
  building_name?: string | null
  customer_id: string
  display_number: number | null
  proposal_id?: string | null
  equipment_id?: string | null
  task_type: PhysicalTaskType | null
  title: string
  description?: string | null
  priority: string
  status: PhysicalTaskStatus
  assigned_to?: string | null
  assignee_user_id?: string | null
  due_date?: string | null
  started_at?: string | null
  completed_at?: string | null
  completion_notes?: string | null
  completion_photos?: string[] | null
  outcome?: string | null
  verified_by?: string | null
  verified_at?: string | null
  estimated_hours?: number | null
  actual_hours?: number | null
  estimated_cost?: number | null
  actual_cost?: number | null
  metadata?: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

/** GET /physical-tasks params (the list-query standard, FO-3b/FO-4). */
export interface PhysicalTaskQuery {
  building_id?: string
  status?: string
  assigned_to?: string
  task_type?: string
  sort?: string
  order?: 'asc' | 'desc'
  filter?: string[]          // <field>:<op>:<value>
  group_by?: string[]
  limit?: number
  offset?: number
}

export interface PhysicalTaskListResponse {
  data: PhysicalTask[]
  total: number
  building_id?: string | null
  status?: string | null
}

/** POST /physical-tasks body. Mirrors backend/app/schemas/physical_task.py::PhysicalTaskCreate. */
export interface PhysicalTaskCreate {
  building_id: string
  title: string
  task_type: PhysicalTaskType
  description?: string | null
  priority?: string
  proposal_id?: string | null
  equipment_id?: string | null
  assigned_to?: string | null
  assignee_user_id?: string | null
  status?: string | null
  due_date?: string | null
  estimated_hours?: number | null
  estimated_cost?: number | null
  metadata?: Record<string, unknown> | null
}

// ============================================
// Field-ops records: comments, attachments, links (FO-4 Task 6)
// Mirrors backend/app/schemas/field_ops_records.py field-for-field.
// ============================================

/** Who did something: a person, the COO agent (on behalf of a person), or a system job. */
export interface ActorOut {
  kind: 'user' | 'coo' | 'system' | 'email' | 'contact'
  id: string
  /** The person's name when the backend resolved it (NomOS: the login's Person, else its member name). */
  name?: string | null
  on_behalf_of?: string | null
}

/** One comment, with its thread position, mentions and resolve/archive state. */
export interface CommentRecord {
  id: string
  customer_id: string
  target_kind: string
  target_id: string
  parent_id?: string | null
  body: string
  mentions: string[]
  source: string
  email_id?: string | null
  created_by: ActorOut
  created_at: string
  resolved_at?: string | null
  resolved_by?: ActorOut | null
  archived_at?: string | null
}

/** An attachment's metadata (never its bytes; fetch those with GET .../download). */
export interface AttachmentMeta {
  id: string
  customer_id: string
  subject_kind: string
  subject_id: string
  filename: string
  mime: string
  bytes: number
  sha256: string
  created_by: ActorOut
  created_at: string
}

/** One stored direction of a link between two records (both directions are stored separately). */
export interface LinkRecord {
  id: string
  customer_id: string
  source_kind: string
  source_id: string
  rel: string
  target_kind: string
  target_id: string
  created_by: ActorOut
  created_at: string
}

/** The result of POST/DELETE /field-ops/links. */
export interface LinkActionResponse {
  rel: string
  source_id: string
  target_id: string
  changed: boolean
  dry_run: boolean
}

// ============================================
// Triage + Inbox (FO-6b, SEN-297)
// Mirrors backend/app/schemas/field_ops_triage.py and field_ops_notifications.py.
// ============================================

/** The list-query standard's page shape: {items, total, next_cursor}. */
export interface ListPage<T> {
  items: T[]
  total: number | null
  next_cursor?: string | null
}

export type TriageReason = 'intake' | 'unrouted'
export type TriageStatus = 'open' | 'dismissed' | 'resolved'

/** One Triage item, list shape (no mail content -- see TriageItemDetail). */
export interface TriageItem {
  id: string
  customer_id: string | null
  email_id: string
  reason: TriageReason
  status: TriageStatus
  from_address: string
  subject: string
  created_by: ActorOut
  created_at: string
  resolved_at?: string | null
  resolved_by?: ActorOut | null
}

/** A Triage item plus the mail's content and attachments. */
export interface TriageItemDetail extends TriageItem {
  to_addresses: string[]
  cc_addresses: string[]
  text_body: string
  html_body: string
  attachments: AttachmentMeta[]
}

export interface TriageAcceptRequest {
  kind: 'task' | 'intervention'
  building_id: string
  title?: string
  description?: string
  task_type?: string
  initiative_type?: string
  assignee_user_id?: string
  owner_user_id?: string
}

export interface TriageAcceptResponse {
  triage_item: TriageItem
  created_kind: 'task' | 'intervention'
  created_id: string
  created_display_number?: number | null
  created_title: string
}

export interface TriageMergeResponse {
  triage_item: TriageItem
  comment_id: string
}

export type NotificationKind = 'comment' | 'status_change' | 'visit_signed_off' | 'assigned' | 'mention'

/** One Inbox row: activity on a task/intervention the caller is involved in (FO-6b). */
export interface Notification {
  id: string
  kind: NotificationKind
  subject_kind: 'task' | 'intervention'
  subject_id: string
  /** The NomOS subject as served (any record type: a mention can be on a site):
   *  its type slug and record id — what opens the right record. */
  subject_type?: string
  subject_record_id?: string
  record_display_number?: number | null
  record_title?: string | null
  payload: Record<string, unknown>
  actor: ActorOut
  read_at?: string | null
  created_at: string
}

// ============================================
// Field-ops contacts, field links, visits (FO-5 Task 7)
// Mirrors backend/app/schemas/field_ops_contacts.py field-for-field.
// ============================================

/** Who may receive a field link — mirrors contacts_service.ROLES. */
export type ContactRole = 'store_manager' | 'auditor' | 'technician' | 'contractor' | 'staff'

/** A field person with no login/license — acts through per-task field links. Archived, never deleted. */
export interface Contact {
  id: string
  customer_id: string
  name: string
  email: string
  phone: string | null
  role: ContactRole
  company: string | null
  created_by: ActorOut
  created_at: string
  archived_at: string | null
}

/** A field link WITHOUT its token — safe to list. Office view. */
export interface FieldLink {
  id: string
  customer_id: string
  task_id: string
  contact_id: string
  expires_at: string
  revoked_at: string | null
  last_used_at: string | null
  created_at: string
}

/** Returned ONCE, right after issue/re-issue: carries the full URL with the raw token. */
export interface FieldLinkIssued extends FieldLink {
  url: string
  /** True once the link was queued for delivery by mail (FO-6, Mailgun configured); false means
   * mail isn't configured here and the url must be copied by hand. */
  emailed: boolean
}

/** The check-in/sign-off state of one contact's visit on a task. */
export interface Visit {
  id: string
  field_link_id: string
  task_id: string
  contact_id: string
  checked_in_at: string | null
  signed_off_at: string | null
  signed_off_name: string | null
  record_sha256: string | null
}

// ============================================
// Building Type Ramp Profiles
// ============================================

// SEN-693: ramp hours are owned by the backend. There is no client-side ramp
// table any more — `GET /settings/building-type-profiles` returns both the
// effective values and the platform defaults, and every zone classification
// happens server-side (see the OperatingZone* types below).
export interface BuildingTypeProfile {
  building_type: string
  ramp_up_hours: number
  ramp_down_hours: number
  building_count: number
  /** Platform default for this building type (before any customer override). */
  default_ramp_up_hours: number
  default_ramp_down_hours: number
  /** True when the customer has overridden the platform default. */
  is_override: boolean
}

// ============================================
// Operating Zones (SEN-693)
// ============================================

/** Fuels the canonical operating-zone endpoint can screen. */
export type EnergySourceCode =
  | 'electricity'
  | 'district_heating'
  | 'natural_gas'
  | 'water'

/** Server-assigned zone for one (dow, hour) cell. */
export type OperatingZone = 'open' | 'ramp_up' | 'ramp_down' | 'closed'

/** One cell of the canonical 7×24 grid, in BUILDING-LOCAL time. */
export interface OperatingZoneCell {
  /** ISO weekday: Mon=1 … Sun=7. */
  dow: number
  /** Local hour-of-day, 0–23. */
  hour: number
  /** Null when the source has no value for this (dow, hour): an empty cell, never 0 kWh. */
  avg_kwh: number | null
  /** Distinct clock-hours behind the mean. Null when the source keeps no count (NomOS's time-part
   *  fold answers the mean only): the count is UNKNOWN, not zero, and the cell is still data. */
  sample_count: number | null
  /** Null when the building has no configured opening hours. */
  zone: OperatingZone | null
}

/** One building row of `GET /analytics/portfolio-load-summary`. */
export interface OperatingZoneBuilding {
  building_id: string
  building_name: string
  country_code: string | null
  building_type: string | null
  size_sqm: number | null
  timezone: string
  has_opening_hours: boolean
  missing_opening_hours: boolean
  /**
   * SEN-693 hotfix #2: false when the building has no AUTOMATIC hourly
   * metering (`data_sources.is_manual = FALSE`) for this fuel/period — its
   * only sources are manual/modelled, typically a monthly total spread evenly
   * across the month's hours. Such a series is perfectly flat (stddev 0) and
   * has no hourly shape, so every zone rate is null and the row is excluded
   * from the ranking. Render the metering gap, never a flat grid.
   */
  metered_hourly: boolean
  ramp_up_hours: number
  ramp_down_hours: number
  ramp_source: 'customer_override' | 'default'
  // Zone-classified totals over the period
  total_open_kwh: number
  total_ramp_up_kwh: number
  total_ramp_down_kwh: number
  total_closed_kwh: number
  total_off_hours_kwh: number
  total_kwh: number
  closed_pct: number | null
  // Rate metrics (kWh per clock-hour, mean of cell means)
  avg_open_kwh_h: number | null
  avg_ramp_up_kwh_h: number | null
  avg_ramp_down_kwh_h: number | null
  avg_closed_kwh_h: number | null
  base_load_kwh_h: number | null
  peak_load_kwh_h: number | null
  closed_w_per_m2: number | null
  hours_open: number
  hours_ramp_up: number
  hours_ramp_down: number
  hours_closed: number
  closed_sample_count: number
  /**
   * Populated ONLY when the request sets include_cells=true; null/absent
   * otherwise. SEN-693 review (C1): the server stopped emitting an empty
   * array per building because it pushed the canonical AI-chat call past the
   * 16 000-char tool-response cap. Always read it as `zone?.cells ?? []`.
   */
  cells?: OperatingZoneCell[] | null
  avg_daily_kwh: number
  base_load_kw_per_sqm?: number | null
  adjusted_kwh?: number | null
  graddage_17_actual?: number | null
  graddage_17_normal?: number | null
  graddage_ratio_pct?: number | null
}

export interface OperatingZoneResponse {
  start_date: string
  end_date: string
  energy_source: string
  dow_filter: string
  ranked_by: string
  top_n: number | null
  buildings: OperatingZoneBuilding[]
  missing_opening_hours_count: number
  /** In-scope buildings with `metered_hourly: false` — full scope, not just the ranked slice. */
  unmetered_count: number
  calculated_at: string
  definitions: Record<string, unknown>
}

// ============================================
// Business Case Types
// ============================================

// ============================================
// ENTSO-E Electricity Market Types
// ============================================

export interface BiddingZone {
  code: string
  name: string
  country_code: string
  timezone: string
}

export interface PricePoint {
  timestamp_utc: string
  day_ahead_eur_mwh: number
  day_ahead_eur_kwh: number
}

export interface PriceSummary {
  avg_eur_mwh: number | null
  min_eur_mwh: number | null
  max_eur_mwh: number | null
  avg_eur_kwh: number | null
  min_eur_kwh: number | null
  max_eur_kwh: number | null
}

export interface PriceResponse {
  bidding_zone: string
  start_date: string
  end_date: string
  hours_in_range: number
  hours_with_data: number
  coverage_pct: number
  prices: PricePoint[]
  summary: PriceSummary
}

export interface CarbonIntensityPoint {
  timestamp_utc: string
  carbon_g_per_kwh: number
  renewable_pct: number
  generation_mix: Record<string, number>
}

export interface CarbonIntensityResponse {
  bidding_zone: string
  start_date: string
  end_date: string
  data: CarbonIntensityPoint[]
}

// SEN-401: period-selector brush data
export interface PeriodSelectorBar {
  week_start: string  // ISO date string (YYYY-MM-DD)
  total_kwh: number
}

export interface AvailableRange {
  start: string  // ISO date string
  end: string    // ISO date string
}

export interface PeriodSelectorSeriesResponse {
  available_range: AvailableRange | null
  bars: PeriodSelectorBar[]
}

export interface NoovaValidateResponse {
  valid: boolean
  error: string | null
  asset_count: number
  electricity_meter_count: number
}

export interface NoovaConnectionCreate {
  name: string
  username: string
  password: string
  customer_id: string
}

export interface NoovaConnectionResponse {
  id: string
  name: string
  status: string
  last_sync_at: string | null
  last_error: Record<string, unknown> | null
  created_at: string
  noova_customer_id: string | null
  username: string | null
  meter_count: number
}

export interface NoovaConnectionListResponse {
  connections: NoovaConnectionResponse[]
  total_count: number
}

export interface NoovaMeteringPoint {
  metering_point_id: string
  meter_number: string | null
  price_area: string | null
  metering_direction: string | null
  asset_legacy_id: string
  asset_name: string
  node_id: string
}

export interface NoovaMatchedStore {
  jysk_code: string
  building_id: string
  building_name: string
  asset_names: string[]
  metering_points: NoovaMeteringPoint[]
}

export interface NoovaDiscoveryReport {
  matched: NoovaMatchedStore[]
  noova_without_building: { jysk_code: string; asset_name: string }[]
  buildings_without_noova: { jysk_code: string; building_id: string; building_name: string }[]
  assets_without_code: string[]
  assets_without_electricity: string[]
  duplicate_codes: Record<string, string[]>
  asset_count: number
  electricity_meter_count: number
}

export interface NoovaLinkResult {
  meters_created: number
  sources_upserted: number
  export_meters: number
}

// Slim building row from GET /buildings/index: enough to label, search and filter.
export type BuildingIndexItem = Pick<
  Building,
  'id' | 'name' | 'country_code' | 'building_type' | 'street_address' | 'postal_code' | 'city'
>

export interface BuildingIndexResponse {
  data: BuildingIndexItem[]
  total: number
}

// COO automations (GET /api/v1/coo/runs, /coo/runs/{id}, /coo/egress-denials).
//
// `kind` + `source` are the shared shape the Automations run table is built
// on generically: today only `kind: 'coo'` exists, `source` mirrors `job`.
// Later run kinds (connection_sync, platform_job) reuse this same row shape
// and column set — see backend/app/schemas/coo.py.
export type CooRunKind = 'coo'
export type CooRunStatus = 'running' | 'completed' | 'failed'

export interface CooRun {
  run_id: string
  kind: CooRunKind
  source: string
  customer_id: string
  job: string
  status: CooRunStatus
  trigger_source: string | null
  started_at: string
  completed_at: string | null
  tool_calls: number
  failed_tool_calls: number
  error_code: string | null
  // Who used Sentinel in a chat run (never what they wrote); null for automation runs.
  acting_user_name: string | null
  // Set when a user pressed Stop (POST /coo/runs/{id}/stop); the run then ends
  // failed with error_code 'stopped_by_user'.
  stop_requested_at: string | null
  stopped_by_name: string | null
}

export interface CooToolCall {
  seq: number
  started_at: string
  tool: string
  method: string
  path: string
  status_code: number | null
  ok: boolean
  duration_ms: number | null
  error_code: string | null
}

export interface CooRunDetail {
  run_id: string
  kind: CooRunKind
  source: string
  customer_id: string
  job: string
  status: CooRunStatus
  trigger_source: string | null
  started_at: string
  completed_at: string | null
  failed_tool_calls: number
  error_code: string | null
  stop_requested_at: string | null
  stopped_by_name: string | null
  trigger_reasoning: string | null
  details: Record<string, unknown>
  errors_by_class: Record<string, { count: number }>
  tool_calls: CooToolCall[]
}

export interface CooEgressDenial {
  id: number
  run_id: string | null
  occurred_at: string
  host: string
  port: number | null
  reason: string
}

export interface CooStopResult {
  run_id: string
  stop_requested_at: string
}

// Sentinel chat (GET/POST /api/v1/coo/chat/threads...). Threads are private to the person.
export type CooMessageStatus = 'complete' | 'streaming' | 'stopped' | 'failed'

export interface CooThread {
  id: string
  title: string
  created_at: string
  updated_at: string
  /** True while Sentinel is answering in this thread. */
  running: boolean
  /** The automation that delivers its results to this thread; null for a conversation the person started. */
  automation_id: string | null
}

/** An automation (backend AutomationOut, /api/v1/coo/automations): a result Sentinel produces on a schedule. */
export interface CooAutomation {
  id: string
  name: string
  result_description: string
  example: string | null
  /** Five cron fields in Copenhagen time. */
  schedule: string
  schedule_text: string
  /** UTC; null when off. */
  next_run_at: string | null
  recipients: { id: string; name: string }[]
  model: string
  /** Stored explicitly: an automation created with "all read tools" lists every read tool. */
  allowed_tools: string[]
  monthly_token_ceiling: number | null
  tokens_this_month: number
  enabled: boolean
  /** proposed = waiting for a person to approve it. */
  status: 'proposed' | 'enabled' | 'disabled'
  created_by_name: string | null
  approved_by_name: string | null
  approved_at: string | null
  last_run: { run_id: string; status: string; error_code: string | null; started_at: string } | null
}

/** Body of POST /coo/automations (backend AutomationIn). Empty recipient_ids = the caller; empty allowed_tools = all read tools. */
export interface CooAutomationInput {
  name: string
  result_description: string
  example: string | null
  schedule: string
  recipient_ids: string[]
  allowed_tools: string[]
  model: string
  monthly_token_ceiling: number | null
}

/** GET /coo/automations/options: what an automation may use. */
export interface CooAutomationOptions {
  tools: { name: string; method: string; path: string; description: string }[]
  recipients: { id: string; name: string; email: string; role: string }[]
  models: string[]
  presets: { schedule: string; label: string }[]
}

export interface CooMessageToolCall {
  tool: string
  ok: boolean
  /** NomOS: the step as the chat shows it, from the tool's served presentation metadata. */
  call_id?: string | null
  /** What the step did ("Searched the catalogue for “kWh”"). */
  label?: string | null
  /** One line about its result ("12 entries: …", or the refusal and its message). */
  summary?: string | null
  duration_ms?: number | null
  /** On expand: the exact parameters and a small preview of the result (bounded by the server). */
  detail?: { params?: unknown; preview?: unknown } | null
}

export interface CooMessage {
  id: number
  seq: number
  role: 'user' | 'assistant'
  content: string
  status: CooMessageStatus
  error_code: string | null
  created_at: string
  finished_at: string | null
  tool_calls: CooMessageToolCall[]
  /** Automations Sentinel proposed while writing this answer. */
  automation_ids: string[]
  /** Files on this message: the person's attachments, or files Sentinel produced (older API: absent). */
  files?: CooFile[]
  /**
   * Charts and tables the answer's `show` tool calls stored (NomOS; validated server-side). Rendered
   * from this structure, never parsed from the text. Older API: absent.
   */
  blocks?: CooBlockSpec[]
  /** Visible grounding flags, e.g. "Answer mentions a file but none was produced". */
  flags?: CooMessageFlag[]
  /**
   * NomOS: the writes this answer PREVIEWED, each a card the person applies or dismisses (built by the
   * server from the tool result, never from the model's text). Older API: absent.
   */
  confirmations?: CooConfirmation[]
  /**
   * NomOS (owner 29 Oct 2026): this answer is a SCHEDULED run of the conversation — which schedule and
   * when it fired; its question is the schedule's instruction. Absent/null for the person's own turns.
   */
  schedule?: CooMessageSchedule | null
}

/** The schedule a scheduled run's answer came from (served with the stored answer). */
export interface CooMessageSchedule {
  automation_id: string
  automation_run_id?: string | null
  fired_at?: string | null
  cadence?: string | null
}

/** How a person undoes one applied write of a confirmation card. */
export interface CooConfirmationUndo {
  /** cell: restore what the write replaced (cell lineage); clear: a first value is cleared again;
   *  record: a created record is archived again */
  kind: 'cell' | 'record' | 'clear'
  /** cell: the cell to restore; clear: the cell the card wrote (cleared only while it is still current) */
  cell_id?: string
  type_id?: string
  record_id?: string
  attribute_id?: string
}

/** A write the agent previewed in the chat (monad/agent/ui_cards.json `confirmation`). */
export interface CooConfirmation {
  kind: 'confirmation'
  confirmation_id: string
  tool: string
  title: string
  description?: string | null
  /** The values the change sets, labelled. */
  values: Array<{ name: string; label: string; value: string }>
  /** The cells it changes: old -> new in words; with the kind and raw values Poiesis renders read cells. */
  changes: Array<{ record: string; field: string; old?: string | null; value?: string | null; kind?: string | null;
                   config?: Record<string, unknown> | null; old_value?: unknown; new_value?: unknown }>
  creates: number
  writes: number
  /** The records it affects, by name. */
  affected: Array<{ record_id: string; kenning: string }>
  /** What Apply does under the person's own governance: apply at once, or submit for an admin's review. */
  on_apply: 'apply' | 'submit_for_review'
  phase?: number | null
  status: 'pending' | 'applying' | 'applied' | 'proposed' | 'refused' | 'failed' | 'dismissed' | 'expired' | 'undone'
  expires_at: string
  outcome?: {
    code?: string
    message?: string
    proposed_change_id?: string | null
    undo?: CooConfirmationUndo[]
    /** The record or dashboard the applied write created or changed. */
    result_id?: string | null
  } | null
}

/** A json-render block spec (the shape of components/coo/blocks/catalog.ts). */
export interface CooBlockSpec {
  root: string
  elements: Record<string, { type: string; props: Record<string, unknown>; children?: string[] }>
}

export interface CooMessageFlag {
  code: string
  message: string
}

// POST /coo/files, GET /coo/files/{id}: a file of the caller's own private chat.
export interface CooFile {
  id: string
  name: string
  content_type: string
  size_bytes: number
  source: 'upload' | 'sentinel'
  created_at: string
}

export interface CooThreadDetail extends CooThread {
  messages: CooMessage[]
}

export type CooChatEvent =
  | { event: 'turn'; data: { thread_id: string; message_id: number; run_id: string } }
  | { event: 'delta'; data: { text: string } }
  | { event: 'tool'; data: { tool: string; phase: 'started' | 'finished'; ok: boolean | null; call_id?: string | null;
      /** started: what it is doing ("Asking the data…"); finished: what it did */
      label?: string | null; summary?: string | null; duration_ms?: number | null } }
  | { event: 'block'; data: { spec: CooBlockSpec } }
  | { event: 'done'; data: { status: 'complete' | 'stopped' | 'failed'; error_code: string | null; message_id: number } }

// GET /coo/status, POST /coo/pause, POST /coo/resume
export interface CooStatus {
  paused: boolean
  paused_at: string | null
  paused_by_name: string | null
}
